import type { VoucherView } from '@voll-intelligence/domain';

import { t } from '@web/i18n';
import { AppIcons, IconButton } from '@web/ui/core/atoms';
import { StatusBadge, voucherStatusBadge } from '@web/ui/core/molecules';
import { Drawer, VoucherDetailSections } from '@web/ui/core/organisms';

import { voucherHeadline, voucherServiceDate, voucherTypeIcon } from '../voucher_presentation';
import { voucherTimeline } from '../voucher_timeline';
import { VoucherTimeline } from './voucher_timeline';

/**
 * The voucher detail drawer — the single interaction model for the Viagens and
 * Cancelados tabs. Opened by any `CustomerVoucherRow`, it renders the locator,
 * a hero (type icon + route + modal/date + status badge), the per-modal detail
 * sections and the derived status timeline. Data-driven from the selected
 * voucher; `voucher === null` keeps it closed. The caller owns the open state.
 */
export function CustomerVoucherDrawer({
  voucher,
  onClose,
}: Readonly<{ voucher: VoucherView | null; onClose: () => void }>): React.JSX.Element {
  const badge = voucher ? voucherStatusBadge(voucher) : null;
  const subtitle = voucher
    ? [voucher.modal, voucherServiceDate(voucher)].filter(Boolean).join(' · ')
    : '';

  return (
    <Drawer
      open={voucher !== null}
      onClose={onClose}
      ariaLabel={t('customers.voucher.drawer.ariaLabel')}
    >
      {voucher ? (
        <>
          <div className="flex flex-shrink-0 items-center gap-3 border-b border-border-soft px-5 py-4">
            <span className="flex-1 truncate font-mono text-card-id font-medium tracking-eyebrow-narrow text-faint-foreground">
              {voucher.voucherCode ?? '—'}
            </span>
            <IconButton
              icon="close"
              variant="outline"
              onClick={onClose}
              title={t('customers.voucher.drawer.close')}
              aria-label={t('customers.voucher.drawer.close')}
            />
          </div>

          <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-5 py-5">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md border border-border-soft bg-muted text-muted-foreground">
                <AppIcons name={voucherTypeIcon(voucher)} size={19} />
              </span>
              <div className="min-w-0">
                <div className="font-serif text-drawer-title leading-card-title text-foreground">
                  {voucherHeadline(voucher)}
                </div>
                {subtitle ? (
                  <div className="mt-px font-mono text-micro text-muted-foreground">{subtitle}</div>
                ) : null}
              </div>
              {badge ? (
                <span className="ml-auto flex-shrink-0">
                  <StatusBadge tone={badge.tone}>{t(badge.labelKey)}</StatusBadge>
                </span>
              ) : null}
            </div>

            <VoucherDetailSections voucher={voucher} />

            <section className="flex flex-col gap-2">
              <span className="font-mono text-tiny font-medium uppercase tracking-eyebrow-wide text-faint-foreground">
                {t('customers.voucher.timeline.label')}
              </span>
              <VoucherTimeline steps={voucherTimeline(voucher)} />
            </section>
          </div>
        </>
      ) : null}
    </Drawer>
  );
}
